import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import {
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import { axiosPath, path } from '../../../../routing/helpers/router';
import Translator from '../../../components/helpers/Translator';
import colors from '../../../../constants/colors';
import errorHandler from '../../../components/error/ErrorHandler';

export default function CommunicationPipelineProgressStep(props) {
  const { report, pageStep, statusProgress, progressInfo } = props;
  const { moduleBridgeHex, reportHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedProgress, setSelectedProgress] = useState();
  const [thread, setThread] = useState();

  const handleSaveStatus = useCallback(async () => {
    if (
      report?.evidences.length === 0 &&
      statusProgress?.value === 'completed' &&
      !['sign', 'allegations'].includes(pageStep)
    ) {
      setSelectedProgress(undefined);
      AppNotificationAlert({
        title: ReactDOMServer.renderToString(
          <Translator file="module-communicationPipeline" name="notice" />
        ),
        message: ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="mark-must-add-least-one-piece-evidence"
          />
        ),
        type: 'warning',
        duration: 5000,
        position: 'top-right',
      });
      return;
    }

    axios
      .post(
        axiosPath(
          'api_r_user_module_communicationPipeline_admin_report_status_edit'
        ),
        {
          moduleBridgeHex,
          reportHex,
          step: pageStep,
          progress: statusProgress?.value,
        }
      )
      .then((r) => {
        if (r?.data?.thread) {
          setSelectedProgress(undefined);
          setThread(r?.data?.thread);
        } else {
          navigate(
            path({
              name: 'user_module_communicationPipeline_admin_report_status',
              params: {
                moduleBridgeHex,
                reportHex,
              },
            })
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex, pageStep, report]);

  const handleChangeProgress = useCallback(() => {
    if (progressInfo?.isReadOnly) return;

    setSelectedProgress(statusProgress?.value);
  }, [statusProgress, progressInfo]);

  const getTitle = useCallback(() => {
    let title;
    if (
      !progressInfo?.isFaint &&
      progressInfo?.isReadOnly &&
      statusProgress?.value === report?.status.progress
    ) {
      title = `${ReactDOMServer.renderToString(
        <Translator file="module-communicationPipeline" name="this-status-is" />
      )} '${statusProgress?.label}'`;
    } else if (progressInfo?.isFaint && !progressInfo?.isReadOnly) {
      title = `${ReactDOMServer.renderToString(
        <Translator file="module-communicationPipeline" name="mark-as" />
      )} '${statusProgress?.label}'`;
    }

    return title;
  }, [report, statusProgress, progressInfo]);

  const date = useCallback(() => {
    if (report?.status?.date?.[pageStep]?.[statusProgress?.value]) {
      return (
        <small>
          {report?.status?.date?.[pageStep]?.[statusProgress?.value]}
        </small>
      );
    }

    return null;
  }, [report, pageStep, statusProgress]);

  const goToThread = useCallback(() => {
    navigate(
      path({
        name: 'user_module_communicationPipeline_admin_report_communication',
        params: { moduleBridgeHex, reportHex, threadHex: thread?.hex },
      })
    );
  }, [moduleBridgeHex, reportHex, thread]);

  return (
    <>
      <Container
        title={getTitle()}
        $color={statusProgress?.color}
        $isFaint={progressInfo?.isFaint}
        $isReadOnly={progressInfo?.isReadOnly}
        onClick={handleChangeProgress}
      >
        <div className="timeline-content">
          <div className="inner-circle" />
          <span className="d-block text-muted mb-0 mb-lg-0">
            {statusProgress?.label}
          </span>
          {date()}
        </div>
      </Container>
      <AppModal
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="modify-progress"
          />
        )}
        show={!!selectedProgress}
        isConfirmBtn
        isCancelBtn
        onClose={() => setSelectedProgress(undefined)}
        onConfirm={handleSaveStatus}
        confirmBtnText={ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="modify-status"
          />
        )}
      >
        <AppRemixIcon icon="alert" color={colors.danger} />
        <span>
          <Translator
            file="module-communicationPipeline"
            name="modify-progress-status-irreversible-process-make-modify"
          />
          .
        </span>
      </AppModal>
      <AppModal
        show={!!thread}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-communicationPipeline"
            name="new-communication"
          />
        )}
        isCloseButton={false}
      >
        <ThreadCodeContainer>
          <h5 className="mb-3">{thread?.name}</h5>
          <span
            className="btn btn-success btn-sm btn-block"
            onClick={goToThread}
          >
            <Translator
              file="module-communicationPipeline"
              name="go-to-communication"
            />
          </span>
        </ThreadCodeContainer>
      </AppModal>
    </>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0.1rem;
  cursor: ${(props) =>
    !props.$isReadOnly
      ? 'pointer'
      : props.$isReadOnly && props.$isFaint
      ? 'not-allowed'
      : 'initial'};

  span {
    font-weight: ${(props) => (props.$isFaint ? 'normal' : 'bold')};
  }

  @media (min-width: 768px) {
    &:not(:last-child):after {
      content: '';
      display: block;
      border-top: 0.25rem dotted ${colors.primaryLight};
      width: 2.8rem;
      position: absolute;
      left: 7.5rem;
      top: 0.3125rem;
    }
    &:not(:first-child):before {
      content: '';
      display: block;
      border-top: 0.25rem dotted ${colors.primaryLight};
      width: 2.5rem;
      position: absolute;
      right: 7.5rem;
      top: 0.3125rem;
    }
    .timeline-content {
      width: 10rem;
      text-align: center;

      .inner-circle {
        border-radius: 1.5rem;
        height: 1rem;
        width: 1rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: ${(props) =>
          props.$color ? colors[props.$color] : colors.primaryLight};
        opacity: ${(props) => (props.$isFaint ? '0.3' : '1')};

        &:before {
          content: '';
          background-color: ${(props) =>
            props.$color ? colors[props.$color] : colors.primaryLight};
          display: inline-block;
          height: 3rem;
          width: 3rem;
          min-width: 3rem;
          border-radius: 6.25rem;
          opacity: ${(props) => (props.$color ? '0.5' : '0.3')};
        }
      }
    }
  }
`;

const ThreadCodeContainer = styled.div`
  text-align: center;
  h3 {
    font-size: 24px;
  }
`;
